/**
 * Hapi, bottom right, on every screen inside the app.
 *
 * He is two things at once and that is the point of putting him here rather
 * than in a corner of the Today page. He is the way into the assistant — a
 * character you talk to reads as something you can ask a half-formed question,
 * where a button marked "AI" reads as something you have to use correctly. And
 * he is the app's only continuous reaction: everything else that tells you
 * something worked is a toast that is gone in three seconds, and none of it
 * accumulates into a sense that the thing you are using is pleased with you.
 *
 * Three rules from the mascot kit shape what is here:
 *   - One Hapi per frame, so this is mounted once, in the shell.
 *   - Give him room: his tail reaches past his body, so nothing around him
 *     clips, and the hit target is a circle rather than a tight box.
 *   - `point` aims at something real — he faces back into the page from the
 *     right edge, which is what `flip` is doing here.
 *
 * Two other things already live in this corner, and he gives way to both
 * rather than fighting them:
 *   - The toast stack comes up from the bottom right. It is lifted clear of
 *     him in ToastContext, at the same breakpoint he appears at, because a
 *     mascot under a stack of toasts is a mascot being sat on.
 *   - The assistant drawer slides in over this corner. He steps left by its
 *     width while it is open, so he stays visible and stays the way to close
 *     it — a launcher that disappears under the thing it launched leaves
 *     somebody hunting for the X.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { Hapi, type HapiPose } from './hapi/Hapi'
import { useUI } from '../context/UIContext'
import { onMood } from '../lib/hapiMood'

/**
 * Whether the canvas behind him is dark, as the page has actually resolved it.
 *
 * Not `useAppearance().theme`: that holds the *preference*, and the preference
 * is often `system`, which is neither answer. `applyToDom` writes `data-theme`
 * for an explicit choice and removes it for system — so the resolved value is
 * the attribute if there is one and the OS setting if there is not. Getting
 * this wrong draws an ink-coloured Hapi on an ink-coloured background, which
 * is not a subtle failure.
 */
function useDarkCanvas(): boolean {
  const read = () => {
    const chosen = document.documentElement.getAttribute('data-theme')
    if (chosen === 'dark') return true
    if (chosen === 'light') return false
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  }

  const [dark, setDark] = useState(read)

  useEffect(() => {
    const update = () => setDark(read())
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    media.addEventListener('change', update)
    // The attribute changes without any event of its own when somebody picks a
    // theme in Settings, so the element is watched directly.
    const watching = new MutationObserver(update)
    watching.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => {
      media.removeEventListener('change', update)
      watching.disconnect()
    }
  }, [])

  return dark
}

/** How long a reaction is held before he settles, when nothing says otherwise. */
const HELD_MS = 2600

/**
 * How long of nobody doing anything before he dozes off.
 *
 * Long enough that it reads as "you went for lunch" rather than "the app got
 * bored of you". He wakes on any pointer or key.
 */
const DOZE_MS = 4 * 60 * 1000

/**
 * The assistant drawer's width, which he steps aside by while it is open.
 *
 * `max-w-md` on the drawer, in pixels. Written down rather than measured: a
 * ResizeObserver on another component to place a mascot is a lot of machinery
 * for a number that is in the drawer's own class list, and the two only ever
 * appear together above `lg`, where the drawer is always at its maximum.
 */
const DRAWER_PX = 448

export function HapiBuddy() {
  const { assistantOpen, setAssistantOpen } = useUI()
  const dark = useDarkCanvas()
  const [pose, setPose] = useState<HapiPose>('idle')
  const [says, setSays] = useState<string | null>(null)
  const settle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(
    () =>
      onMood((mood) => {
        setPose(mood.pose)
        setSays(mood.says ?? null)
        clearTimeout(settle.current)
        settle.current = setTimeout(() => {
          setPose('idle')
          setSays(null)
        }, mood.ms ?? HELD_MS)
      }),
    [],
  )

  useEffect(() => () => clearTimeout(settle.current), [])

  // Dozing. Deliberately not wired through the mood bus: this is about the
  // absence of events, which nothing can send.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const wake = () => {
      setPose((p) => (p === 'sleepy' ? 'idle' : p))
      clearTimeout(timer)
      timer = setTimeout(() => {
        // Only from rest. Interrupting a celebration to fall asleep would be
        // both rude and, worse, confusing about what just happened.
        setPose((p) => (p === 'idle' ? 'sleepy' : p))
      }, DOZE_MS)
    }
    wake()
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'wheel']
    for (const e of events) window.addEventListener(e, wake, { passive: true })
    return () => {
      clearTimeout(timer)
      for (const e of events) window.removeEventListener(e, wake)
    }
  }, [])

  // While the drawer is open he is working it out, and he is not in the way.
  const working = assistantOpen

  return (
    <motion.div
      // Stepping aside for the drawer rather than being covered by it. The
      // spring matches the drawer so the two move together instead of him
      // chasing it.
      animate={{ right: (assistantOpen ? DRAWER_PX : 0) + 16 }}
      transition={{ type: 'spring', stiffness: 300, damping: 32 }}
      className="pointer-events-none fixed bottom-4 z-[150] hidden select-none lg:block"
    >
      <AnimatePresence>
        {says !== null && !working && (
          <motion.p
            initial={{ opacity: 0, y: 6, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 420, damping: 28 }}
            // Tail on the bottom right, pointing down at him, now that he is
            // the thing to the right of it.
            className="mb-1 mr-2 ml-auto w-max max-w-[15rem] rounded-2xl rounded-br-md border border-shell-border bg-shell px-3 py-1.5 text-xs font-medium text-shell-text-active shadow-lg"
          >
            {says}
          </motion.p>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setAssistantOpen(!assistantOpen)}
        aria-label={assistantOpen ? 'Close the assistant' : 'Ask Hapi'}
        title={assistantOpen ? 'Close the assistant' : 'Ask Hapi'}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="pointer-events-auto grid h-24 w-24 place-items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        {/* Flipped so he faces back into the page, not off the right edge. */}
        <Hapi
          pose={working ? 'think' : pose}
          size={92}
          colorway={dark ? 'dark' : 'light'}
          flip
          label="Ask Hapi"
        />
      </motion.button>
    </motion.div>
  )
}
